/**
 * MappingsOSDReportModal — Over, Short & Damaged report.
 * Opens from the Receiving Shipment screen (Warehouse Management System > Receiving Document),
 * next to "Upload Shipment Document".
 *
 * Unlike a typical react-bootstrap <Modal>, this is a self-contained overlay: the form has four
 * tabs, a print-friendly layout, and its own focus/ESC handling, so it is rendered directly by
 * the parent (mounted only while open) rather than wrapped in react-bootstrap's <Modal>.
 *
 * Data contract: the parent already has the shipment and its PO items loaded (this screen fetches
 * them for the page itself), so this component takes them as props instead of re-fetching a
 * "shipment details" endpoint. There is no carrier/BOL/trailer/seal data in this system (it is a
 * PO-receiving warehouse app, not a freight/LTL tracking app) — those fields are optional manual
 * entries here, useful only when the user is filing a carrier claim.
 *
 * Save contract — POST ReceivingShipment/save-osd-report (ReceivingShipmentService.saveOsdReport)
 *   Body matches Chemplast.Raintels.Entity.DataModel.SaveOSDReportRequest.
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';
import { ReceivingShipmentService } from '../../service/api/receiving-shipment-service';
import { EntraUserMultiSelect, CarrierSelect } from '../../core/components';
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';

/* ------------------------------------------------------------------ */
/* option lists                                                        */
/* ------------------------------------------------------------------ */

const EXCEPTION_TYPES = [
  'Overage', 'Shortage', 'Visible damage', 'Concealed damage', 'Wrong item',
  'Wrong destination', 'Load refused', 'Late delivery', 'Other',
];
const DIRECTIONS = ['Outbound', 'Inbound', 'Transfer'];
const SEAL_CONDITIONS = ['Intact', 'Broken', 'Missing'];
const ASN_STATUSES = ['Accurate', 'Correction required', 'Cancel and resend'];
const DAMAGE_KINDS = [
  'Crushed', 'Punctured or torn', 'Water or moisture', 'Contamination',
  'Leaking or spilled', 'Load shift', 'Pallet failure', 'Temperature exposure', 'Mislabeled',
];
const PACKAGING = [
  'Gaylord', 'Super sack', 'Bag', 'Drum', 'Carton', 'Returnable container', 'Pallet or stretch wrap',
];
const USABLE = ['Yes', 'Partial', 'No'];
const NOTED = ['Yes', 'No', 'Not applicable'];
const YES_NO = ['Yes', 'No'];
const CLAIM = ['Yes', 'No', 'Pending'];
const ACTIONS = [
  'Replacement shipped', 'Credit memo issued', 'Debit memo to supplier',
  'Return to supplier', 'Rework', 'Scrap', 'Held for carrier inspection',
  'Billed back to carrier', 'Accepted as is',
];
const FAULT = [
  'Carrier', 'Chemplast shipping', 'Chemplast production or packaging', 'Supplier',
  'Customer receiving', 'System or data entry', 'Undetermined',
];

/** Informational signature slots shown alongside "Prepared by" — not collected on this form. */
const APPROVERS = [
  { role: 'Shipping supervisor', status: 'pending' },
  { role: 'Quality (contamination, NCR, or lot recall)', status: 'na' },
  { role: 'Purchasing or customer service', status: 'pending' },
  { role: 'Controller (claims over threshold)', status: 'na' },
];

const TABS = [
  'Exception & carrier',
  'Order & quantities',
  'Damage & disposition',
  'Root cause & signature',
];

const BLANK_LINES = 2;

const today = () => new Date().toISOString().slice(0, 10);
const cx = (...c) => c.filter(Boolean).join(' ');

/* ------------------------------------------------------------------ */
/* small building blocks                                               */
/* ------------------------------------------------------------------ */

function Field({ label, span, required, locked, invalid, danger, children }) {
  return (
    <div className={cx('osd-f', span && `osd-s${span}`, locked && 'osd-locked', invalid && 'osd-invalid')}>
      {label && (
        <label className={cx(required && 'osd-req')} style={danger ? { color: 'var(--osd-red)' } : undefined}>
          {label}
        </label>
      )}
      {children}
    </div>
  );
}

function Locked({ label, span, value, type = 'text' }) {
  return (
    <Field label={label} span={span} locked>
      {type === 'text'
        ? <div className="osd-lockbox">{value || ' '}</div>
        : <input type={type} value={value ?? ''} readOnly tabIndex={-1} />}
    </Field>
  );
}

function CheckGroup({ options, selected = [], forced = [], onToggle, danger }) {
  return (
    <div className="osd-checks">
      {options.map((o) => {
        const isForced = forced.includes(o);
        const checked = isForced || selected.includes(o);
        return (
          <label key={o} className={cx('osd-chk', danger && 'osd-chk--danger', isForced && 'osd-chk--forced')}
                 title={isForced ? 'Set from the quantity variance' : undefined}>
            <input type="checkbox" checked={checked} disabled={isForced}
                   onChange={() => onToggle(o)} />
            {o}
          </label>
        );
      })}
    </div>
  );
}

function RadioGroup({ name, options, value, onChange, danger }) {
  return (
    <div className="osd-checks">
      {options.map((o) => (
        <label key={o} className={cx('osd-chk', danger && 'osd-chk--danger')}>
          <input type="radio" name={name} value={o} checked={value === o}
                 onChange={() => onChange(o)} />
          {o}
        </label>
      ))}
    </div>
  );
}

function Block({ title, note, children }) {
  return (
    <section className="osd-block">
      <div className="osd-bhead">
        <h2>{title}</h2>
        {note && <span className="osd-note">{note}</span>}
      </div>
      {children}
    </section>
  );
}

function PillBox({ label, children }) {
  return (
    <div className="osd-pillbox">
      <div className="osd-plab">{label}</div>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* main component                                                      */
/* ------------------------------------------------------------------ */

export default function MappingsOSDReportModal({
  receivingShipmentId,
  shipment,
  vendorName = '',
  poNumbers = [],
  parts = [],
  onClose,
  onSaved,
}) {
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [f, setF] = useState({ discDate: '', reportDate: today() });
  const [types, setTypes] = useState([]);
  const [damage, setDamage] = useState([]);
  const [packaging, setPackaging] = useState([]);
  const [actions, setActions] = useState([]);
  const [lines, setLines] = useState([]);
  const [errors, setErrors] = useState([]);
  const [flash, setFlash] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [osdReportId, setOsdReportId] = useState(0);
  const [osdNo, setOsdNo] = useState('');
  const tabRefs = useRef([]);

  const sessionUser = useMemo(() => ({
    displayName: localStorage.getItem('UserName') || '',
    email: localStorage.getItem('UserEmail') || '',
  }), []);

  const set = useCallback((k, v) => setF((p) => ({ ...p, [k]: v })), []);
  const toggle = (setter) => (v) =>
    setter((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));

  const blankLines = (startAt) => Array.from({ length: BLANK_LINES }, (_, i) => ({
    id: `m-${startAt + i}`, source: 'manual', partNo: '', description: '',
    lot: '', uom: '', qtyAsn: '', qtyActual: '', condition: '',
  }));

  const seedFromParts = useCallback(() => {
    const fromParts = (parts || []).map((p, i) => ({
      id: `sp-${i}`, source: 'sp',
      partNo: p.partNumber || p.itemName || '',
      description: p.description || p.itemName || '',
      lot: '', uom: p.uom || p.unit || '',
      qtyAsn: p.quantityRequested != null ? String(p.quantityRequested) : '',
      qtyActual: '', condition: '',
    }));
    setLines([...fromParts, ...blankLines(fromParts.length)]);
    if (shipment?.status) set('direction', 'Inbound');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts, shipment]);

  const applyExistingReport = useCallback((detail) => {
    setOsdReportId(detail.osdReportId || detail.OSDReportId || 0);
    setOsdNo(detail.osdNo || detail.OsdNo || '');
    setF({
      direction: detail.direction, discDate: detail.discoveredOn || '', discTime: detail.discoveredTime,
      discLoc: detail.discoveredLocation, sealCond: detail.sealCondition,
      carrierId: detail.carrierId, carrier: detail.carrier, scac: detail.scac, pro: detail.proTrackingNumber, bol: detail.bolNumber,
      trailer: detail.trailer, sealNo: detail.sealNumber, freightTerms: detail.freightTerms, driver: detail.driver,
      asnStatus: detail.asnStatus, asnNote: detail.asnCorrectionNote,
      unitsAffected: detail.unitsAffected, palletsAffected: detail.palletsAffected, lossValue: detail.estimatedLossValue,
      usable: detail.materialUsable, noted: detail.notedOnDeliveryReceipt, notedText: detail.notedText,
      driverSigned: detail.driverSigned, photos: detail.photosAttached, photoLocation: detail.photoLocation,
      narrative: detail.narrative, invAdjNo: detail.inventoryAdjustmentNo, memoNo: detail.memoNo,
      rmaNo: detail.rmaNo, ncrNo: detail.ncrNo, claim: detail.carrierClaimFiled, claimNo: detail.claimNo,
      claimDate: detail.claimDate || '', claimAmount: detail.claimAmount, partyNotified: detail.partyNotified,
      carrierContact: detail.carrierContact, fault: detail.fault, rootCause: detail.rootCause,
      preventiveAction: detail.preventiveAction, attest: !!detail.attestation, driverAck: detail.driverAckReference,
      reportDate: detail.reportDate || today(),
      witnesses: (detail.witnesses || []).map((w) => ({ id: w.entraUserId, displayName: w.displayName })),
    });
    setTypes(detail.exceptionTypes || []);
    setDamage(detail.damage || []);
    setPackaging(detail.packaging || []);
    setActions(detail.actions || []);
    const savedLines = (detail.lines || []).map((l, i) => ({
      id: `${l.source === 'sp' ? 'sp' : 'm'}-${i}`, source: l.source || 'manual',
      partNo: l.partNo || '', description: l.description || '', lot: l.lot || '', uom: l.uom || '',
      qtyAsn: l.qtyExpected != null ? String(l.qtyExpected) : '',
      qtyActual: l.qtyActual != null ? String(l.qtyActual) : '',
      condition: l.conditionNote || '',
    }));
    setLines([...savedLines, ...blankLines(savedLines.length)]);
  }, []);

  /* ---- load a previously saved report for this shipment, or seed a blank one from the PO items ---- */
  useEffect(() => {
    let alive = true;
    if (!receivingShipmentId) { seedFromParts(); setLoadingExisting(false); return; }
    setLoadingExisting(true);
    ReceivingShipmentService.getOsdReport(receivingShipmentId)
      .then((res) => {
        if (!alive) return;
        const detail = res?.status === 200 ? res.response : null;
        if (detail) applyExistingReport(detail);
        else seedFromParts();
      })
      .catch(() => { if (alive) seedFromParts(); })
      .finally(() => { if (alive) setLoadingExisting(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivingShipmentId]);

  const setLine = (id, col, v) =>
    setLines((p) => p.map((l) => (l.id === id ? { ...l, [col]: v } : l)));

  /* ---- variance ---- */
  const computed = useMemo(() => {
    let totAsn = 0, totActual = 0, counted = 0, anyShort = false, anyOver = false;
    const rows = lines.map((l) => {
      const a = parseFloat(l.qtyAsn), b = parseFloat(l.qtyActual);
      if (!Number.isNaN(a)) totAsn += a;
      if (!Number.isNaN(b)) { totActual += b; counted += 1; }
      if (Number.isNaN(a) || Number.isNaN(b)) return { ...l, variance: null };
      const d = +(b - a).toFixed(4);
      if (d < 0) anyShort = true;
      if (d > 0) anyOver = true;
      return { ...l, variance: d };
    });
    const total = +(totActual - totAsn).toFixed(4);
    const summary = counted === 0 ? ''
      : anyShort && anyOver ? 'Both shortage and overage lines'
      : anyShort ? 'Shortage against the PO'
      : anyOver ? 'Overage against the PO'
      : 'Matches the PO';
    return { rows, totAsn: +totAsn.toFixed(4), totActual: +totActual.toFixed(4), total, counted, anyShort, anyOver, summary };
  }, [lines]);

  /* the numbers decide these two, not the person filling the form */
  const forcedTypes = useMemo(() => [
    ...(computed.anyShort ? ['Shortage'] : []),
    ...(computed.anyOver ? ['Overage'] : []),
  ], [computed.anyShort, computed.anyOver]);

  const effectiveTypes = useMemo(
    () => Array.from(new Set([...types, ...forcedTypes])), [types, forcedTypes]);

  const reportedLines = useMemo(
    () => computed.rows.filter((l) => l.qtyActual.trim() || l.condition.trim()), [computed.rows]);

  /* ---- validation ---- */
  const validate = useCallback(() => {
    const p = [];
    if (!effectiveTypes.length) p.push({ tab: 0, msg: 'Pick at least one exception type.' });
    if (!f.direction) p.push({ tab: 0, msg: 'Pick the shipment direction.' });
    if (!f.discDate) p.push({ tab: 0, msg: 'Enter the date the exception was discovered.', field: 'discDate' });
    if (!f.sealCond) p.push({ tab: 0, msg: 'Record the seal condition.' });
    if (!f.asnStatus) p.push({ tab: 1, msg: 'Set the paperwork status.' });
    if (!reportedLines.length) p.push({ tab: 1, msg: 'Enter at least one actual quantity or condition note.' });
    if (damage.length && !f.noted)
      p.push({ tab: 2, msg: 'Damage is recorded — answer whether it was noted on the delivery receipt.', field: 'noted' });
    if (!f.attest) p.push({ tab: 3, msg: 'Check the attestation before saving.' });
    return p;
  }, [effectiveTypes, f, reportedLines, damage]);

  const badField = (name) => errors.some((e) => e.field === name);
  const badTab = (i) => errors.some((e) => e.tab === i);

  /* ---- build the API payload (matches SaveOSDReportRequest) ---- */
  const buildRequest = useCallback(() => ({
    osdReportId,
    receivingShipmentID: receivingShipmentId,
    shipmentNumber: shipment?.shipmentNumber,
    direction: f.direction,
    discoveredOn: f.discDate,
    discoveredTime: f.discTime,
    discoveredLocation: f.discLoc,
    witnesses: (f.witnesses || []).map((w, i) => ({
      sortOrder: i,
      entraUserId: w.id,
      displayName: w.displayName,
    })),
    sealCondition: f.sealCond,
    carrierId: f.carrierId || null,
    carrier: f.carrier,
    scac: f.scac,
    proTrackingNumber: f.pro,
    bolNumber: f.bol,
    trailer: f.trailer,
    sealNumber: f.sealNo,
    freightTerms: f.freightTerms,
    driver: f.driver,
    asnStatus: f.asnStatus,
    asnCorrectionNote: f.asnNote,
    unitsAffected: f.unitsAffected,
    palletsAffected: f.palletsAffected,
    estimatedLossValue: f.lossValue,
    materialUsable: f.usable,
    notedOnDeliveryReceipt: f.noted,
    notedText: f.notedText,
    driverSigned: f.driverSigned,
    photosAttached: f.photos,
    photoLocation: f.photoLocation,
    narrative: f.narrative,
    inventoryAdjustmentNo: f.invAdjNo,
    memoNo: f.memoNo,
    rmaNo: f.rmaNo,
    ncrNo: f.ncrNo,
    carrierClaimFiled: f.claim,
    claimNo: f.claimNo,
    claimDate: f.claimDate,
    claimAmount: f.claimAmount,
    partyNotified: f.partyNotified,
    carrierContact: f.carrierContact,
    fault: f.fault,
    rootCause: f.rootCause,
    preventiveAction: f.preventiveAction,
    attestation: !!f.attest,
    driverAckReference: f.driverAck,
    reportDate: f.reportDate,
    reportedBy: sessionUser.displayName,
    reportedByEmail: sessionUser.email,
    exceptionTypes: effectiveTypes,
    damage, packaging, actions,
    lines: reportedLines.map((l) => ({
      source: l.source, partNo: l.partNo, description: l.description, lot: l.lot, uom: l.uom,
      qtyExpected: l.qtyAsn === '' ? null : Number(l.qtyAsn),
      qtyActual: l.qtyActual === '' ? null : Number(l.qtyActual),
      variance: l.variance,
      conditionNote: l.condition,
    })),
  }), [osdReportId, receivingShipmentId, shipment, f, sessionUser, effectiveTypes, damage, packaging, actions, reportedLines]);

  const handleSave = async () => {
    const problems = validate();
    setErrors(problems);
    if (problems.length) {
      setTab(problems[0].tab);
      setFlash({ error: true, msg: problems[0].msg + (problems.length > 1 ? `  (+${problems.length - 1} more)` : '') });
      return;
    }
    if (!receivingShipmentId) {
      setFlash({ error: true, msg: 'Shipment is missing — reopen the report from the shipment screen.' });
      return;
    }

    const body = buildRequest();
    setSaving(true);
    try {
      const res = await ReceivingShipmentService.saveOsdReport(body);
      if (res?.status === 200 && res?.response) {
        const savedId = res.response.osdReportId || res.response.OSDReportId || osdReportId;
        const savedNo = res.response.osdNo || res.response.OsdNo || osdNo;
        setOsdReportId(savedId);
        setOsdNo(savedNo);
        dispatch(showToast({
          message: `OS&D report ${osdReportId ? 'updated' : 'saved'}${savedNo ? ` as ${savedNo}` : ''}.`,
          type: 'success',
        }));
        onSaved?.({ ...body, osdReportId: savedId, osdNo: savedNo });
        onClose?.();
      } else {
        const msg = res?.errorMessage?.message || 'Failed to save the OS&D report.';
        setFlash({ error: true, msg });
        dispatch(showToast({ message: msg, type: 'danger' }));
      }
    } catch (e) {
      const msg = `Save failed (${e.message}).`;
      setFlash({ error: true, msg });
      dispatch(showToast({ message: msg, type: 'danger' }));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 6000);
    return () => clearTimeout(t);
  }, [flash]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onTabKey = (e, i) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); const n = (i + 1) % TABS.length; setTab(n); tabRefs.current[n]?.focus(); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); const n = (i - 1 + TABS.length) % TABS.length; setTab(n); tabRefs.current[n]?.focus(); }
  };

  const stamp = useMemo(() => new Date().toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }), []);

  const varClass = (v) => v == null ? 'osd-var osd-var--zero'
    : v === 0 ? 'osd-var osd-var--zero' : v < 0 ? 'osd-var osd-var--short' : 'osd-var osd-var--over';
  const varText = (v) => v == null ? '—' : v > 0 ? `+${v}` : String(v);

  return (
    <div className="osd-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <style>{CSS}</style>
      <div className="osd-modal" role="dialog" aria-modal="true" aria-labelledby="osd-title"
           onMouseDown={(e) => e.stopPropagation()}>

        <header className="osd-head">
          <h1 id="osd-title">OS&amp;D Report</h1>
          <div className="osd-ref">
            Shipment <b>{shipment?.shipmentNumber}</b>
            {vendorName ? <> · {vendorName}</> : null}
            {osdNo ? <> · OS&amp;D <b>{osdNo}</b></> : null}
          </div>
          <span className="osd-sp" />
          <button className="osd-x" onClick={onClose} aria-label="Close OS&D report">×</button>
        </header>

        {loadingExisting && <LoadingSpinner />}

        <div className="osd-tabs" role="tablist" aria-label="OS&D sections">
          {TABS.map((t, i) => (
            <button key={t} ref={(el) => (tabRefs.current[i] = el)}
                    className={cx('osd-tab', badTab(i) && 'osd-tab--issue')}
                    role="tab" aria-selected={tab === i} aria-controls={`osd-p${i}`}
                    tabIndex={tab === i ? 0 : -1}
                    onClick={() => setTab(i)} onKeyDown={(e) => onTabKey(e, i)}>
              <span className="osd-tnum">{i + 1}</span>{t}
              <span className="osd-flag" />
            </button>
          ))}
        </div>

        <div className="osd-body">
          {/* ---------------- 1 ---------------- */}
          <div className="osd-panel" id="osd-p0" role="tabpanel" data-hidden={tab !== 0}>
            <Block title="Exception type and reporter" note="Check every condition found">
              <div className="osd-grid osd-g4">
                <Field label="Exception found" span={2} required>
                  <div className="osd-pillbox">
                    <CheckGroup options={EXCEPTION_TYPES} selected={types} forced={forcedTypes}
                                onToggle={toggle(setTypes)} />
                  </div>
                </Field>
                <Locked label="Reported by" value={sessionUser.displayName} />
                <Locked label="Report date" value={f.reportDate} type="date" />
                <Field label="Direction" required>
                  <div className="osd-pillbox">
                    <RadioGroup name="direction" options={DIRECTIONS} value={f.direction}
                                onChange={(v) => set('direction', v)} />
                  </div>
                </Field>
                <Field label="Discovered on" required invalid={badField('discDate')}>
                  <input type="date" value={f.discDate} onChange={(e) => set('discDate', e.target.value)} />
                </Field>
                <Field label="Time">
                  <input type="time" value={f.discTime ?? ''} onChange={(e) => set('discTime', e.target.value)} />
                </Field>
              </div>
              <div className="osd-grid osd-g4" style={{ marginTop: 13 }}>
                <Field label="Discovered at (dock / location)" span={2}>
                  <input type="text" placeholder="Dock 4, staging lane B"
                         value={f.discLoc ?? ''} onChange={(e) => set('discLoc', e.target.value)} />
                </Field>
                <Field label="Who else was present" span={2}>
                  <EntraUserMultiSelect
                    value={f.witnesses || []}
                    onChange={(list) => set('witnesses', list)}
                    placeholder="Search by name…"
                  />
                </Field>
              </div>
            </Block>

            <Block title="Shipment" note="From the receiving record — edit only what the paperwork contradicts">
              <div className="osd-grid osd-g4">
                <Locked label="Shipment number" value={shipment?.shipmentNumber} />
                <Locked label="Vendor / supplier" value={vendorName} />
                <Locked label="Shipment status" value={shipment?.status} />
                <Locked label="Shipment date" value={shipment?.shipmentDate} />
                <Field label="Seal condition at arrival" required span={2}>
                  <div className="osd-pillbox">
                    <RadioGroup name="sealCond" options={SEAL_CONDITIONS} value={f.sealCond}
                                onChange={(v) => set('sealCond', v)} />
                  </div>
                </Field>
                <Field label="Driver name">
                  <input type="text" placeholder="As printed on the receipt"
                         value={f.driver ?? ''} onChange={(e) => set('driver', e.target.value)} />
                </Field>
              </div>
            </Block>

            <Block title="Carrier details" note="Optional — fill in only if this is going into a carrier claim">
              <div className="osd-grid osd-g4">
                <Field label="Carrier">
                  <CarrierSelect
                    value={f.carrierId ? { id: f.carrierId, name: f.carrier } : null}
                    onChange={(sel) => { set('carrierId', sel?.id || null); set('carrier', sel?.name || ''); }}
                  />
                </Field>
                <Field label="SCAC">
                  <input type="text" value={f.scac ?? ''} onChange={(e) => set('scac', e.target.value)} />
                </Field>
                <Field label="PRO / tracking no.">
                  <input type="text" value={f.pro ?? ''} onChange={(e) => set('pro', e.target.value)} />
                </Field>
                <Field label="BOL no.">
                  <input type="text" value={f.bol ?? ''} onChange={(e) => set('bol', e.target.value)} />
                </Field>
                <Field label="Trailer / container">
                  <input type="text" value={f.trailer ?? ''} onChange={(e) => set('trailer', e.target.value)} />
                </Field>
                <Field label="Seal no.">
                  <input type="text" value={f.sealNo ?? ''} onChange={(e) => set('sealNo', e.target.value)} />
                </Field>
                <Field label="Freight terms">
                  <input type="text" value={f.freightTerms ?? ''} onChange={(e) => set('freightTerms', e.target.value)} />
                </Field>
              </div>
            </Block>
          </div>

          {/* ---------------- 2 ---------------- */}
          <div className="osd-panel" id="osd-p1" role="tabpanel" data-hidden={tab !== 1}>
            <Block title="Order reference" note="Receiving — read only">
              <div className="osd-grid osd-g4">
                <Locked label="Shipment number" value={shipment?.shipmentNumber} />
                <Locked label="Vendor / supplier" value={vendorName} />
                <Locked label="Purchase order(s)" value={poNumbers.join(', ')} span={2} />
                <Field label="Comments" span={4}>
                  <textarea rows={2} value={shipment?.comments || ''} readOnly tabIndex={-1} />
                </Field>
                <Field label="Paperwork / ASN status" required>
                  <div className="osd-pillbox">
                    <RadioGroup name="asnStatus" options={ASN_STATUSES} value={f.asnStatus}
                                onChange={(v) => set('asnStatus', v)} />
                  </div>
                </Field>
                <Field label="Correction note to the vendor" span={3}>
                  <input type="text" placeholder="What changes, and who transmits it"
                         value={f.asnNote ?? ''} onChange={(e) => set('asnNote', e.target.value)} />
                </Field>
              </div>
            </Block>

            <Block title="Quantity detail" note="Enter the actual count — variance calculates against the PO">
              <table className="osd-table">
                <thead>
                  <tr>
                    <th style={{ width: '20%' }}>Part / item</th>
                    <th style={{ width: '14%' }}>Lot / batch</th>
                    <th style={{ width: '7%' }}>UOM</th>
                    <th className="osd-n" style={{ width: '11%' }}>Qty per PO</th>
                    <th className="osd-n" style={{ width: '13%' }}>Qty actual</th>
                    <th className="osd-n" style={{ width: '11%' }}>Variance</th>
                    <th style={{ width: '24%' }}>Condition / note</th>
                  </tr>
                </thead>
                <tbody>
                  {computed.rows.map((l) => {
                    const sp = l.source === 'sp';
                    return (
                      <tr key={l.id}>
                        <td>
                          {sp ? (
                            <div className="osd-cellro">{l.partNo}<small>{l.description}</small></div>
                          ) : (
                            <input type="text" placeholder="Part not on the PO" value={l.partNo}
                                   onChange={(e) => setLine(l.id, 'partNo', e.target.value)} />
                          )}
                        </td>
                        <td><input type="text" value={l.lot} onChange={(e) => setLine(l.id, 'lot', e.target.value)} /></td>
                        <td>{sp ? <span className="osd-cellro">{l.uom}</span> : (
                          <input type="text" className="osd-qty" style={{ width: 56 }} value={l.uom}
                                 onChange={(e) => setLine(l.id, 'uom', e.target.value)} />)}</td>
                        <td className="osd-n">{sp ? <span className="osd-cellro">{l.qtyAsn}</span> : (
                          <input type="text" className="osd-qty" value={l.qtyAsn}
                                 onChange={(e) => setLine(l.id, 'qtyAsn', e.target.value)} />)}</td>
                        <td className="osd-n">
                          <input type="text" className="osd-qty" inputMode="decimal" value={l.qtyActual}
                                 onChange={(e) => setLine(l.id, 'qtyActual', e.target.value)} />
                        </td>
                        <td className="osd-n"><span className={varClass(l.variance)}>{varText(l.variance)}</span></td>
                        <td>
                          <input type="text" className="osd-cond" placeholder="e.g. 2 cartons crushed"
                                 value={l.condition} onChange={(e) => setLine(l.id, 'condition', e.target.value)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>Totals</td>
                    <td className="osd-n"><b>{computed.totAsn}</b></td>
                    <td className="osd-n"><b>{computed.totActual}</b></td>
                    <td className="osd-n">
                      <b className={varClass(computed.counted === 0 ? null : computed.total)}>
                        {computed.counted === 0 ? '—' : varText(computed.total)}
                      </b>
                    </td>
                    <td>{computed.summary}</td>
                  </tr>
                </tfoot>
              </table>
              <div className="osd-callout" style={{ marginTop: 12 }}>
                <b>Count against the PO, not the pick list.</b> A variance here is what will hit the vendor's and our books.
                Leave a line blank if it was not part of this exception.
              </div>
            </Block>
          </div>

          {/* ---------------- 3 ---------------- */}
          <div className="osd-panel" id="osd-p2" role="tabpanel" data-hidden={tab !== 2}>
            <Block title="Damage and packaging detail" note="Skip if the exception is quantity only">
              <div className="osd-grid osd-g2">
                <PillBox label="Nature of damage">
                  <CheckGroup options={DAMAGE_KINDS} selected={damage} onToggle={toggle(setDamage)} />
                </PillBox>
                <PillBox label="Packaging affected">
                  <CheckGroup options={PACKAGING} selected={packaging} onToggle={toggle(setPackaging)} />
                </PillBox>
              </div>
              <div className="osd-grid osd-g4" style={{ marginTop: 13 }}>
                <Field label="Units affected">
                  <input type="text" value={f.unitsAffected ?? ''} onChange={(e) => set('unitsAffected', e.target.value)} />
                </Field>
                <Field label="Pallets affected">
                  <input type="text" value={f.palletsAffected ?? ''} onChange={(e) => set('palletsAffected', e.target.value)} />
                </Field>
                <Field label="Estimated loss (USD)">
                  <input type="text" placeholder="0.00" value={f.lossValue ?? ''} onChange={(e) => set('lossValue', e.target.value)} />
                </Field>
                <Field label="Material still usable">
                  <div className="osd-pillbox">
                    <RadioGroup name="usable" options={USABLE} value={f.usable} onChange={(v) => set('usable', v)} />
                  </div>
                </Field>
                <Field span={2} required danger invalid={badField('noted')}
                       label="Exception written on the delivery receipt before the driver left">
                  <div className="osd-pillbox">
                    <RadioGroup name="noted" options={NOTED} value={f.noted} onChange={(v) => set('noted', v)} danger />
                  </div>
                </Field>
                <Field label="Driver signed the exception">
                  <div className="osd-pillbox">
                    <RadioGroup name="driverSigned" options={YES_NO} value={f.driverSigned} onChange={(v) => set('driverSigned', v)} />
                  </div>
                </Field>
                <Field label="Photos attached">
                  <div className="osd-pillbox">
                    <RadioGroup name="photos" options={YES_NO} value={f.photos} onChange={(v) => set('photos', v)} />
                  </div>
                </Field>
                <Field label="Exact wording on the delivery receipt" span={2}>
                  <input type="text" value={f.notedText ?? ''} onChange={(e) => set('notedText', e.target.value)} />
                </Field>
                <Field label="Photo count and file location" span={2}>
                  <input type="text" placeholder="/Shipment/OSD/…" value={f.photoLocation ?? ''}
                         onChange={(e) => set('photoLocation', e.target.value)} />
                </Field>
                <Field label="What happened, in your own words" span={4}>
                  <textarea rows={3} value={f.narrative ?? ''} onChange={(e) => set('narrative', e.target.value)} />
                </Field>
              </div>
              <div className="osd-callout osd-callout--stop">
                <b>Before the driver leaves.</b> Count and inspect at the dock, write the shortage, overage, or damage on the
                delivery receipt, and have the driver sign it. Photograph the load before anything moves.
              </div>
            </Block>

            <Block title="Disposition" note="Shipping supervisor completes this">
              <PillBox label="Action taken">
                <CheckGroup options={ACTIONS} selected={actions} onToggle={toggle(setActions)} />
              </PillBox>
              <div className="osd-grid osd-g4" style={{ marginTop: 13 }}>
                {[
                  ['invAdjNo', 'Inventory adjustment no.'],
                  ['memoNo', 'Credit / debit memo no.'],
                  ['rmaNo', 'RMA no.'],
                  ['ncrNo', 'NCR / CAR no.'],
                ].map(([k, label]) => (
                  <Field key={k} label={label}>
                    <input type="text" value={f[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
                  </Field>
                ))}
                <Field label="Carrier claim filed">
                  <div className="osd-pillbox">
                    <RadioGroup name="claim" options={CLAIM} value={f.claim} onChange={(v) => set('claim', v)} />
                  </div>
                </Field>
                <Field label="Claim no.">
                  <input type="text" value={f.claimNo ?? ''} onChange={(e) => set('claimNo', e.target.value)} />
                </Field>
                <Field label="Claim filed on">
                  <input type="date" value={f.claimDate ?? ''} onChange={(e) => set('claimDate', e.target.value)} />
                </Field>
                <Field label="Claim amount (USD)">
                  <input type="text" value={f.claimAmount ?? ''} onChange={(e) => set('claimAmount', e.target.value)} />
                </Field>
                <Field label="Vendor notified by / on" span={2}>
                  <input type="text" value={f.partyNotified ?? ''} onChange={(e) => set('partyNotified', e.target.value)} />
                </Field>
                <Field label="Carrier contact and reference" span={2}>
                  <input type="text" value={f.carrierContact ?? ''} onChange={(e) => set('carrierContact', e.target.value)} />
                </Field>
              </div>
            </Block>
          </div>

          {/* ---------------- 4 ---------------- */}
          <div className="osd-panel" id="osd-p3" role="tabpanel" data-hidden={tab !== 3}>
            <Block title="Root cause and prevention">
              <PillBox label="Responsibility">
                <RadioGroup name="fault" options={FAULT} value={f.fault} onChange={(v) => set('fault', v)} />
              </PillBox>
              <div className="osd-grid osd-g2" style={{ marginTop: 13 }}>
                <Field label="Root cause">
                  <textarea rows={3} value={f.rootCause ?? ''} onChange={(e) => set('rootCause', e.target.value)} />
                </Field>
                <Field label="Preventive action, owner, and due date">
                  <textarea rows={3} value={f.preventiveAction ?? ''} onChange={(e) => set('preventiveAction', e.target.value)} />
                </Field>
              </div>
            </Block>

            <Block title="Signature" note="Prepared-by is signed by your session">
              <div className="osd-sigwrap">
                <div className="osd-sigcard osd-sigcard--me">
                  <div className="osd-sigtop">
                    <div className="osd-role">Prepared by</div>
                    <span className="osd-status osd-status--signed">Signed</span>
                  </div>
                  <div className="osd-sig-blank" aria-hidden="true" />
                  {sessionUser.displayName && <div className="osd-who">{sessionUser.displayName}</div>}
                  {sessionUser.email && <div className="osd-meta">{sessionUser.email}</div>}
                  <div className="osd-meta">{`Signed electronically ${stamp}`}</div>
                </div>
                {APPROVERS.map((a) => (
                  <div className="osd-sigcard" key={a.role}>
                    <div className="osd-sigtop">
                      <div className="osd-role">{a.role}</div>
                      <span className={cx('osd-status', a.status === 'na' ? 'osd-status--na' : 'osd-status--pending')}>
                        {a.status === 'na' ? 'Not required' : 'Awaiting signature'}
                      </span>
                    </div>
                    <div className="osd-sig-blank" aria-hidden="true" />
                  </div>
                ))}
              </div>
              <div className="osd-grid osd-g2" style={{ marginTop: 13 }}>
                <Field label="Attestation" required>
                  <div className="osd-checks">
                    <label className="osd-chk">
                      <input type="checkbox" checked={!!f.attest} onChange={(e) => set('attest', e.target.checked)} />
                      I counted or verified the quantities and conditions recorded above.
                    </label>
                  </div>
                </Field>
                <Field label="Driver acknowledgement reference">
                  <input type="text" placeholder="Receipt no. or signature capture ID"
                         value={f.driverAck ?? ''} onChange={(e) => set('driverAck', e.target.value)} />
                </Field>
              </div>
            </Block>
          </div>
        </div>

        <footer className="osd-foot">
          {flash && <span className={cx('osd-flash', flash.error && 'osd-flash--error')}>{flash.msg}</span>}
          <span className="osd-sp" />
          <button className="osd-btn" onClick={() => window.print()} disabled={loadingExisting}>Print</button>
          <button className="osd-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="osd-btn osd-btn--primary" onClick={handleSave} disabled={saving || loadingExisting}>
            {saving ? 'Saving…' : osdReportId ? 'Update OS&D report' : 'Save OS&D report'}
          </button>
        </footer>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* scoped styles                                                       */
/* ------------------------------------------------------------------ */

const CSS = `
.osd-overlay{
  --osd-navy:#17365D; --osd-ink:#33425B; --osd-muted:#6B7A90; --osd-faint:#98A5B6;
  --osd-orange:#EF8022; --osd-orange-hi:#F79A4B; --osd-ring:rgba(239,128,34,.30);
  --osd-line:#E5E9F0; --osd-line-soft:#EEF1F6; --osd-fill:#F6F8FB; --osd-lock:#F2F5F9;
  --osd-red:#D0342C; --osd-red-soft:#FDF1F0; --osd-green:#1E7B54;
  --osd-amber:#FFF7E8; --osd-amber-line:#E9B84A;
  position:fixed;inset:0;z-index:1200;display:flex;align-items:flex-start;justify-content:center;
  background:rgba(38,46,58,.55);padding:26px 18px;overflow:auto;
  font-family:'Poppins','Nunito Sans','Segoe UI',system-ui,-apple-system,sans-serif;
  color:var(--osd-ink);font-size:13px;line-height:1.45;
}
.osd-overlay *,.osd-overlay *::before,.osd-overlay *::after{box-sizing:border-box}
.osd-modal{width:100%;max-width:1180px;background:#fff;border-radius:9px;
  box-shadow:0 18px 46px rgba(12,20,34,.34);overflow:hidden}
.osd-sp{flex:1}

.osd-head{display:flex;align-items:center;gap:14px;padding:15px 22px;border-bottom:1px solid var(--osd-line)}
.osd-head h1{margin:0;font-size:17px;font-weight:600;color:var(--osd-navy);letter-spacing:.1px}
.osd-ref{font-size:12px;color:var(--osd-muted);padding-left:14px;border-left:1px solid var(--osd-line)}
.osd-ref b{color:var(--osd-navy);font-weight:600}
.osd-x{border:none;background:none;font-size:22px;line-height:1;color:var(--osd-muted);cursor:pointer;
  padding:0 6px;border-radius:4px}
.osd-x:hover{background:var(--osd-fill);color:var(--osd-navy)}
.osd-x:focus-visible{outline:2px solid var(--osd-orange);outline-offset:1px}

.osd-tabs{display:flex;gap:10px;padding:12px 22px 14px;border-bottom:1px solid var(--osd-line)}
.osd-tab{flex:1;text-align:center;font:inherit;font-size:13px;font-weight:500;color:var(--osd-muted);
  background:none;border:1px solid transparent;border-radius:7px;padding:8px 10px;cursor:pointer}
.osd-tnum{font-weight:600;opacity:.6;margin-right:5px}
.osd-tab:hover{color:var(--osd-navy);background:var(--osd-fill)}
.osd-tab[aria-selected="true"]{background:linear-gradient(180deg,var(--osd-orange-hi),var(--osd-orange));
  color:#fff;border-color:var(--osd-orange);box-shadow:0 0 0 3px var(--osd-ring)}
.osd-tab[aria-selected="true"]:hover{color:#fff}
.osd-tab[aria-selected="true"] .osd-tnum{opacity:.85}
.osd-tab:focus-visible{outline:2px solid var(--osd-navy);outline-offset:2px}
.osd-flag{display:none;width:6px;height:6px;border-radius:50%;background:var(--osd-red);
  margin-left:6px;vertical-align:middle}
.osd-tab--issue .osd-flag{display:inline-block}
.osd-tab--issue[aria-selected="true"] .osd-flag{background:#fff}

.osd-body{padding:18px 22px 4px;min-height:430px}
.osd-panel[data-hidden="true"]{display:none}
.osd-block{margin-bottom:22px}
.osd-block:last-child{margin-bottom:6px}
.osd-bhead{display:flex;align-items:baseline;gap:10px;margin-bottom:11px}
.osd-bhead h2{margin:0;font-size:13.5px;font-weight:600;color:var(--osd-navy)}
.osd-note{font-size:11.5px;color:var(--osd-faint)}
.osd-bhead:after{content:"";flex:1;border-bottom:1px solid var(--osd-line-soft);transform:translateY(-3px)}

.osd-grid{display:grid;gap:13px 18px}
.osd-g2{grid-template-columns:repeat(2,1fr)}
.osd-g4{grid-template-columns:repeat(4,1fr)}
.osd-s2{grid-column:span 2}.osd-s3{grid-column:span 3}.osd-s4{grid-column:span 4}

.osd-f{display:flex;flex-direction:column;min-width:0}
.osd-f > label{font-size:11px;font-weight:500;color:var(--osd-muted);margin-bottom:4px}
.osd-req:after{content:"*";color:var(--osd-orange);margin-left:3px;font-weight:600}
.osd-overlay input[type=text],.osd-overlay input[type=date],.osd-overlay input[type=time],
.osd-overlay textarea,.osd-lockbox{font:inherit;font-size:13px;color:var(--osd-ink);border:1px solid var(--osd-line);
  border-radius:6px;background:#fff;padding:7px 9px;width:100%}
.osd-overlay textarea{resize:vertical;line-height:1.5}
.osd-lockbox{min-height:34px;white-space:pre-wrap;word-break:break-word;box-sizing:border-box}
.osd-overlay input::placeholder,.osd-overlay textarea::placeholder{color:#B6C0CD}
.osd-overlay input:focus,.osd-overlay textarea:focus{outline:none;border-color:var(--osd-orange);
  box-shadow:0 0 0 3px var(--osd-ring)}
.osd-locked input,.osd-locked textarea,.osd-locked .osd-lockbox{background:var(--osd-lock);border-color:var(--osd-line-soft);
  color:var(--osd-navy);font-weight:500}
.osd-locked > label:after{content:"from shipment";font-size:9.5px;font-weight:500;color:var(--osd-faint);
  margin-left:6px;background:var(--osd-fill);border:1px solid var(--osd-line);border-radius:3px;padding:0 4px}
.osd-invalid input,.osd-invalid textarea{border-color:var(--osd-red);background:var(--osd-red-soft)}
.osd-invalid > label{color:var(--osd-red)}

.osd-checks{display:flex;flex-wrap:wrap;gap:7px 16px;align-items:center;padding-top:2px}
.osd-chk{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;cursor:pointer;color:var(--osd-ink)}
.osd-chk input{appearance:none;-webkit-appearance:none;width:15px;height:15px;margin:0;flex:0 0 auto;
  border:1.5px solid #B9C4D2;border-radius:4px;background:#fff;cursor:pointer;
  print-color-adjust:exact;-webkit-print-color-adjust:exact}
.osd-chk input[type=radio]{border-radius:50%}
.osd-chk input:checked{background:var(--osd-orange);border-color:var(--osd-orange);box-shadow:inset 0 0 0 2.5px #fff}
.osd-chk input:disabled{cursor:default;opacity:.85}
.osd-chk input:focus-visible{outline:2px solid var(--osd-navy);outline-offset:2px}
.osd-chk--forced{color:var(--osd-muted)}
.osd-chk--danger{color:var(--osd-red);font-weight:500}
.osd-chk--danger input:checked{background:var(--osd-red);border-color:var(--osd-red)}
.osd-pillbox{border:1px solid var(--osd-line);border-radius:7px;padding:9px 11px}
.osd-plab{font-size:11px;font-weight:600;color:var(--osd-navy);margin-bottom:7px}

.osd-table{width:100%;border-collapse:collapse;font-size:12.5px}
.osd-table thead th{font-size:11px;font-weight:500;color:var(--osd-muted);text-align:left;
  padding:0 10px 9px;border-bottom:1px solid var(--osd-line);white-space:nowrap}
.osd-table tbody td{padding:7px 10px;border-bottom:1px solid var(--osd-line-soft);vertical-align:middle}
.osd-table tbody tr:last-child td{border-bottom:1px solid var(--osd-line)}
.osd-table td.osd-n,.osd-table th.osd-n{text-align:right}
.osd-table td input{padding:5px 7px}
.osd-table td.osd-n input{text-align:right}
.osd-qty{width:82px!important;display:inline-block}
.osd-cond{min-width:120px}
.osd-cellro{color:var(--osd-navy);font-weight:500}
.osd-cellro small{display:block;color:var(--osd-faint);font-weight:400;font-size:11px}
.osd-var{font-weight:600;font-variant-numeric:tabular-nums}
.osd-var--zero{color:var(--osd-faint)}
.osd-var--short{color:var(--osd-red)}
.osd-var--over{color:var(--osd-orange)}
.osd-table tfoot td{padding:9px 10px;font-size:12px;color:var(--osd-muted);background:var(--osd-fill)}
.osd-table tfoot b{color:var(--osd-navy)}

.osd-callout{background:var(--osd-amber);border:1px solid var(--osd-amber-line);border-left-width:3px;
  border-radius:6px;padding:9px 12px;font-size:12px;margin-top:14px;color:#5C4A1E}
.osd-callout--stop{background:var(--osd-red-soft);border-color:#E9A9A4;border-left-color:var(--osd-red);color:#7B2B26}

.osd-sigwrap{display:grid;grid-template-columns:repeat(2,1fr);gap:12px}
.osd-sigcard{border:1px solid var(--osd-line);border-radius:7px;padding:11px 13px;background:#fff}
.osd-sigcard--me{border-color:var(--osd-orange);box-shadow:0 0 0 3px var(--osd-ring)}
.osd-sigtop{display:flex;align-items:center;gap:8px}
.osd-sig-blank{height:30px;border-bottom:1px solid var(--osd-line);margin:6px 0}
.osd-role{font-size:11px;color:var(--osd-muted)}
.osd-who{font-size:13.5px;font-weight:600;color:var(--osd-navy)}
.osd-meta{font-size:11.5px;color:var(--osd-faint);margin-top:2px}
.osd-status{display:inline-flex;align-items:center;font-size:10.5px;font-weight:600;border-radius:11px;
  padding:2px 9px;margin-left:auto;print-color-adjust:exact;-webkit-print-color-adjust:exact}
.osd-status--signed{background:#E7F4EE;color:var(--osd-green)}
.osd-status--pending{background:var(--osd-fill);color:var(--osd-muted)}
.osd-status--na{background:var(--osd-fill);color:var(--osd-faint)}

.osd-foot{display:flex;align-items:center;gap:10px;padding:13px 22px;border-top:1px solid var(--osd-line);
  background:var(--osd-fill)}
.osd-flash{font-size:12px;font-weight:500;color:var(--osd-green)}
.osd-flash--error{color:var(--osd-red)}
.osd-btn{font:inherit;font-size:13px;font-weight:500;padding:8px 17px;border-radius:6px;cursor:pointer;
  border:1px solid var(--osd-line);background:#fff;color:var(--osd-ink)}
.osd-btn:hover{border-color:#C9D3E0}
.osd-btn--primary{background:linear-gradient(180deg,var(--osd-orange-hi),var(--osd-orange));
  border-color:var(--osd-orange);color:#fff}
.osd-btn--primary:hover{filter:brightness(1.05)}
.osd-btn:disabled{opacity:.6;cursor:default}
.osd-btn:focus-visible{outline:2px solid var(--osd-navy);outline-offset:2px}

@media print{
  /* The modal renders inline inside the Receiving Shipment page's component tree, not a portal,
     so without this the whole underlying page prints too, with the report tacked onto the end of
     it. Hide everything on the page, then re-show just this subtree, pulled to the page origin —
     the standard "print only this element" technique. */
  body *{visibility:hidden!important}
  .osd-overlay,.osd-overlay *{visibility:visible!important}
  .osd-overlay{position:absolute!important;top:0;left:0;margin:0;width:100%;
    background:#fff;padding:0;overflow:visible;display:block}
  .osd-modal{box-shadow:none;border-radius:0;max-width:none}
  .osd-head{padding:0 0 8px;border-bottom:2px solid var(--osd-navy)}
  .osd-x,.osd-tabs,.osd-foot{display:none}
  .osd-body{padding:0;min-height:0}
  .osd-panel[data-hidden="true"]{display:block!important}
  .osd-panel{break-after:page}
  .osd-panel:last-of-type{break-after:auto}
  .osd-block{break-inside:avoid;margin-bottom:18px}
  .osd-overlay input[type=text],.osd-overlay input[type=date],.osd-overlay input[type=time],
  .osd-overlay textarea,.osd-lockbox{background:transparent!important;border-color:#9AA5B4!important}
  /* Must stay narrowly scoped to text-like inputs — a bare "input" selector here would also hit
     checkboxes/radios and strip their checked-state orange fill (it has no !important of its own,
     so the broader rule would win and every checkbox/radio would print as empty regardless of
     whether it's actually checked). */
  .osd-locked input,.osd-locked .osd-lockbox{background:#F2F5F9!important}
  /* CarrierSelect / EntraUserMultiSelect print themselves as a plain text box — see those
     components' own print styles — so nothing further is needed for them here. */

  /* Single full-width column per field — a dense multi-column grid reads cramped on a printed
     page, so every field gets its own row instead. */
  .osd-grid{gap:16px 0}
  .osd-g2,.osd-g4{grid-template-columns:1fr}
  .osd-s2,.osd-s3,.osd-s4{grid-column:span 1}
  .osd-sigwrap{grid-template-columns:1fr}
  .osd-overlay input[type=text],.osd-overlay input[type=date],.osd-overlay input[type=time],
  .osd-overlay textarea{padding:9px 10px}
  .osd-checks{gap:9px 22px}
}
@media (max-width:820px){
  .osd-overlay{padding:0}
  .osd-modal{border-radius:0}
  .osd-g2,.osd-g4{grid-template-columns:1fr}
  .osd-s2,.osd-s3,.osd-s4{grid-column:span 1}
  .osd-sigwrap{grid-template-columns:1fr}
  .osd-tabs{flex-wrap:wrap}
  .osd-tab{flex:1 1 45%}
}
@media (prefers-reduced-motion:reduce){.osd-overlay *{transition:none!important;animation:none!important}}
`;
